/**
 * The one way to build an update-`changes` schema from a write schema.
 *
 * `writeSchema.partial()` alone is a trap: zod re-fires `.default()` on every
 * omitted field, so a PATCH that edits one field silently WRITES the defaults
 * of all the others — a title-only contact edit demoted a primary
 * (`isPrimary: false`), and an agreement cost edit would have injected
 * `propertyIds: []` and wiped the coverage snapshot. Full-payload forms masked
 * it; the hierarchy suite caught it. Unwrapping the defaults FIRST restores
 * the PATCH contract: absent means "unchanged", never "the default".
 *
 * STRICT, too. Zod's default is to strip a key the schema does not know, which
 * makes "edit a field that no longer exists" a silent success — 0040 moved the
 * address off `properties`, and a browser tab left open across that deploy
 * would have PATCHed `city`, been told 200, and lost the edit with no sign
 * anything went wrong. A caller naming a field the contract does not have has a
 * bug, and the answer to a bug is a 400, not a shrug.
 */

import { z } from 'zod';

type StripDefault<F> =
  F extends z.ZodDefault<infer Inner extends z.ZodType> ? Inner : F extends z.ZodType ? F : never;

export function partialUpdateSchema<T extends z.ZodRawShape>(
  schema: z.ZodObject<T>,
): z.ZodObject<{ [K in keyof T]: z.ZodOptional<StripDefault<T[K]>> }> {
  const shape = Object.fromEntries(
    Object.entries(schema.shape).map(([key, field]) => [
      key,
      field instanceof z.ZodDefault ? field.unwrap() : field,
    ]),
  );
  return z.strictObject(shape).partial() as unknown as z.ZodObject<{
    [K in keyof T]: z.ZodOptional<StripDefault<T[K]>>;
  }>;
}
